import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { Avatar } from "@fluentui/react-components";
import { ClipboardCopy, EllipsisVertical } from "lucide-react";
import { Button } from "@/components/ui/button";

import {
  ReferenceStatus,
  referenceStatusList,
} from "@/redux/features/candidateProfileSlice";

import { AdminReference } from "@/queries/admin/references";
import { useResendReferenceRequestEmail } from "@/mutations/admin/resend-reference-request-email";
import { toast } from "sonner";

const ReferenceKanban = ({
  references,
  setSelectedReference,
}: {
  references?: AdminReference[];
  setSelectedReference: (v: AdminReference | undefined) => void;
}) => {
  return (
    <div className="flex flex-row gap-2 self-stretch overflow-auto">
      {referenceStatusList.map((status) => (
        <PipelineColumn
          key={`kanban-reference-${status}`}
          status={status}
          references={references}
          setSelectedReference={setSelectedReference}
        />
      ))}
    </div>
  );
};

const PipelineColumn = ({
  status,
  references,
  setSelectedReference,
}: {
  status: ReferenceStatus;
  references?: AdminReference[];
  setSelectedReference: (v: AdminReference | undefined) => void;
}) => {
  const filteredReferences = useMemo(
    () => references?.filter((reference) => reference.status === status),
    [status, references],
  );

  const allowRequest = useMemo(() => {
    return status === "Requested";
  }, [status]);

  let dotColor: string, backgroundColor: string, textColor: string;
  switch (status) {
    case "Requested":
      backgroundColor = "bg-red-100";
      textColor = "text-red-600";
      dotColor = "bg-red-600";
      break;
    case "Approved":
      backgroundColor = "bg-orange-100";
      textColor = "text-orange-600";
      dotColor = "bg-orange-600";
      break;
    case "Rejected":
      backgroundColor = "bg-blue-100";
      textColor = "text-blue-600";
      dotColor = "bg-blue-600";
      break;
  }

  return (
    <div className="min-w-72 p-1 flex-1 flex flex-col gap-2 bg-[#f9f9fb] border border-[#e9eaeb] rounded-[10px]">
      <div className="h-10 py-[21px] px-4 flex flex-row gap-2 self-stretch items-center">
        <div className="flex-1 flex items-center gap-2">
          <div className={cn("h-2.5 w-2.5 rounded-full", dotColor)} />
          <div className="flex items-center gap">
            <p className="text-base text-[#0c0d0d] font-semibold text-nowrap">
              {status}
            </p>
          </div>
          {Boolean(filteredReferences?.length) && (
            <div className="p-2 min-h-2.5 min-w-2.5 flex justify-center align-middle rounded shadow-[0px_3px_12px_0px_rgba(28,28,28,0.20)]">
              <span className="text-xs text-[#0c0d0d] font-semibold tracking-[-0.24px]">
                {filteredReferences?.length}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-start gap-1 self-stretch">
        {filteredReferences?.map((reference) => (
          <PipelineTile
            key={`reference-${reference.id}`}
            reference={reference}
            textColor={textColor}
            backgroundColor={backgroundColor}
            allowRequest={allowRequest}
            selectReference={() => setSelectedReference(reference)}
          />
        ))}
      </div>
    </div>
  );
};

const PipelineTile = ({
  reference,
  backgroundColor,
  textColor,
  allowRequest,
  selectReference,
}: {
  reference: AdminReference;
  backgroundColor: string;
  textColor: string;
  allowRequest: boolean;
  selectReference: () => void;
}) => {
  const { resendReferenceRequestEmail, isResendingReferenceRequestEmail } =
    useResendReferenceRequestEmail(reference.id);

  return (
    <div className="p-4 flex flex-col items-start self-stretch gap-1 bg-white border border-[#e9eaeb] rounded-[10px]">
      <div className="flex flex-row items-center justify-between self-stretch">
        <div className="flex flex-row items-center gap-1">
          <Avatar
            image={{ src: reference.user.profilePictureLink }}
            name={reference.user.name}
            size={24}
          />
          <p className="text-sm text-[#0c0d0d] font-semibold leading-6">
            {reference.user.name}
          </p>
        </div>
        <div className="flex flex-row items-center">
          <div
            className={cn(
              "py-1 px-2.5 flex flex-row items-center gap-2 rounded-full",
              backgroundColor,
            )}
          >
            <p
              className={cn(
                "text-xs font-semibold tracking-[-0.24px]",
                textColor,
              )}
            >
              {reference.status}
            </p>
          </div>
          <EllipsisVertical
            className="cursor-pointer"
            size={16}
            stroke="#B1B9B8"
            onClick={selectReference}
          />
        </div>
      </div>
      <div className="flex flex-col items-start self-stretch gap-1">
        <PipelineInfo title="Reference" value={reference.referenceName} />
        <PipelineInfo title="Title" value={reference.title} />
        <PipelineInfo title="Company" value={reference.company} />
        <PipelineInfo title="Relationship" value={reference.relationship} />
        <PipelineInfo title="Email" value={reference.email} copyValue />
        <PipelineInfo
          title="Survey Link"
          link={reference.surveyLink}
          expectLink
        />
      </div>
      {allowRequest && (
        <Button
          variant="secondary"
          size="sm"
          disabled={isResendingReferenceRequestEmail}
          onClick={() => resendReferenceRequestEmail()}
        >
          Resend Request
        </Button>
      )}
    </div>
  );
};

const PipelineInfo = ({
  title,
  value,
  copyValue,
  link,
  expectLink,
}: {
  title: string;
  value?: string | number;
  copyValue?: boolean;
  link?: string;
  expectLink?: boolean;
}) => {
  if (expectLink && !link) {
    return;
  }

  if (copyValue && !value) {
    return;
  }

  return (
    <div className="flex flex-col items-start self-stretch gap-2 border-b last:border-b-0 border-[rgba(24,24,27,0.12)]">
      <div className="pt-0.5 px-1.5 flex flex-row items-center justify-between self-stretch gap-2">
        <div className="flex flex-row items-center self-stretch gap-1">
          <p className="text-[10px] text-[#141515] font-medium">{title}</p>
        </div>
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-[#646cff] font-medium hover:underline"
          >
            Link
          </a>
        ) : copyValue ? (
          <div
            className="text-xs text-[#07574b] font-medium hover:underline cursor-pointer"
            onClick={() => {
              void navigator.clipboard.writeText(value?.toString() ?? "");
              toast.success("Copied to clipboard");
            }}
          >
            <span className="flex flex-row items-center gap-1">
              Copy
              <ClipboardCopy size={16} />
            </span>
          </div>
        ) : (
          <p className="text-xs text-[#5c6060] font-medium">
            {value ?? "Unknown"}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReferenceKanban;
